import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from 'app/typography';
import Expander from 'components/Containers/Expander';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import { CollapsedButton } from './styles';

const StyledExpander = styled(Expander)`
  margin-bottom: ${({ hasCollapsedButton }) => (hasCollapsedButton ? 19 : 0)}px;
`;

const ExpandableText = styled(KizenTypography)`
  white-space: pre-wrap;
  overflow-wrap: break-word;
  ${({ expanded }) => {
    if (expanded) {
      return css`
        & p:not(:empty) {
          height: unset !important;
        }
        display: block;
      `;
    }

    return css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      max-height: calc(
        1.5em * 3
      ); // 3 lines tall; Fallback in case line-clamp isn't supported because i line is 1.5em right now
    `;
  }};
`;

const CollapsedButtonWrapper = styled.div`
  position: absolute;
  left: 0;
`;

export default function LongTextEllipsis({
  children,
  isExpandable = false,
  ...others
}) {
  const expanderHandle = useRef();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef();
  const [tooltipProps, tooltip] = useTruncationTooltip({}, 'y');

  const toggleExpanded = useCallback(() => {
    setIsExpanded((x) => !x);
  }, []);

  useEffect(() => {
    if (isExpandable) {
      const observeOverflow = () => {
        if (textRef.current && !isExpanded) {
          setIsOverflowed(
            textRef.current.scrollHeight > textRef.current.offsetHeight
          );
        }
      };

      observeOverflow();
      window.addEventListener('resize', observeOverflow);
      return () => {
        window.removeEventListener('resize', observeOverflow);
      };
    }
  }, [isExpandable, isExpanded]);

  if (isExpandable && isOverflowed) {
    return (
      <div>
        <StyledExpander
          ref={expanderHandle}
          closedState="partially-open"
          showArrowBtn={false}
          gradient={null}
          partiallyOpenHeight={63}
          hasCollapsedButton={isExpandable && isOverflowed}
          onChange={toggleExpanded}
        >
          <ExpandableText
            ref={textRef}
            expanded={isExpanded}
            {...(!isExpandable && tooltipProps)}
            {...others}
          >
            {children}
          </ExpandableText>
        </StyledExpander>
        {isExpandable && isOverflowed ? (
          <CollapsedButtonWrapper>
            <CollapsedButton
              variant="text"
              color="blue"
              onClick={() => {
                expanderHandle.current.toggle();
              }}
            >
              {isExpanded ? t('Hide Full Message') : t('View Full Message')}
            </CollapsedButton>
          </CollapsedButtonWrapper>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <ExpandableText
        ref={textRef}
        hasCollapsedButton={isExpandable && isOverflowed}
        expanded={isExpanded}
        {...(!isExpandable && tooltipProps)}
        {...others}
      >
        {children}
      </ExpandableText>
      {!isExpandable && tooltip}
    </div>
  );
}
